import React from "react";
import { usePrefersReducedMotion } from "../components/journey/hooks";
import { useT } from "../i18n/LanguageContext";

function Node({ x, y, w = 132, h = 52, title, sub, accent = "rgba(255,255,255,0.14)" }) {
  return (
    <g aria-hidden="true">
      <rect x={x} y={y} width={w} height={h} rx="10" fill="rgba(255,255,255,0.04)" stroke={accent} strokeWidth="1" />
      <text x={x + w / 2} y={y + (sub ? 23 : 31)} textAnchor="middle" fill="#FFFFFF" fontSize="12.5" fontFamily="'Space Grotesk', sans-serif" fontWeight="600">{title}</text>
      {sub && <text x={x + w / 2} y={y + 39} textAnchor="middle" fill="rgba(255,255,255,0.45)" fontSize="9.5" fontFamily="'Inter', sans-serif">{sub}</text>}
    </g>
  );
}

export default function ChargeHubEnergyFlow({ testId = "chargehub-energy-flow" }) {
  const reduced = usePrefersReducedMotion();
  const t = useT();

  const path = (d, bright = false) => (
    <>
      <path d={d} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="1" />
      <path
        d={d}
        fill="none"
        stroke={bright ? "rgba(33,245,155,0.75)" : "rgba(0,212,255,0.6)"}
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray="3 9"
        className={!reduced ? "chf-dash" : undefined}
      />
    </>
  );

  return (
    <div data-testid={testId} className="bg-[#071A2D] border border-white/10 rounded-2xl p-4 sm:p-6 overflow-hidden">
      <style>{`
        @keyframes chfDash { from { stroke-dashoffset: 48; } to { stroke-dashoffset: 0; } }
        .chf-dash { animation: chfDash 2.6s linear infinite; }
        @media (prefers-reduced-motion: reduce) { .chf-dash { animation: none; } }
      `}</style>

      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="text-[11px] uppercase tracking-widest text-white/40">{t("Site energy coordination · illustrative")}</div>
        <div data-testid="chargehub-flow-status" className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] bg-white/[0.05] border border-white/10 text-white/80">
          <span className="w-1.5 h-1.5 rounded-full bg-[#21F59B]" />
          {t("One coordinated energy system")}
        </div>
      </div>

      <svg
        viewBox="0 0 720 420"
        className="w-full h-auto"
        role="img"
        aria-label="Diagram of WattZap ChargeHub connecting grid supply, solar generation and battery storage with EV chargers as one coordinated energy system"
      >
        {/* sources → hub */}
        {path("M 172 78 C 240 78, 240 190, 288 200")}
        {path("M 172 210 L 288 210", true)}
        {path("M 172 342 C 240 342, 240 230, 288 220")}
        {/* hub → chargers */}
        {path("M 432 200 C 480 190, 480 78, 548 78", true)}
        {path("M 432 210 L 548 210", true)}
        {path("M 432 220 C 480 230, 480 342, 548 342")}

        {/* source nodes */}
        <Node x={40} y={52} title="Electricity Grid" sub="Grid supply" />
        <Node x={40} y={184} title="Solar PV" sub="On-site generation" accent="rgba(33,245,155,0.4)" />
        <Node x={40} y={316} title="Battery Storage" sub="Energy storage" accent="rgba(0,212,255,0.35)" />

        {/* hub */}
        <g aria-hidden="true">
          <rect x={288} y={158} width={144} height={104} rx="14" fill="rgba(0,212,255,0.06)" stroke="rgba(0,212,255,0.55)" strokeWidth="1.5" />
          <text x={360} y={198} textAnchor="middle" fill="#FFFFFF" fontSize="14" fontFamily="'Space Grotesk', sans-serif" fontWeight="700">WattZap</text>
          <text x={360} y={216} textAnchor="middle" fill="#00D4FF" fontSize="13" fontFamily="'Space Grotesk', sans-serif" fontWeight="600">ChargeHub</text>
          <text x={360} y={236} textAnchor="middle" fill="rgba(255,255,255,0.45)" fontSize="9.5" fontFamily="'Inter', sans-serif">Intelligent energy layer</text>
        </g>

        {/* charger nodes + EVs */}
        {[52, 184, 316].map((y, i) => (
          <g key={y} aria-hidden="true">
            <rect x={548} y={y} width={92} height={52} rx="10" fill="rgba(255,255,255,0.04)" stroke="rgba(33,245,155,0.4)" strokeWidth="1" />
            <text x={594} y={y + 23} textAnchor="middle" fill="#FFFFFF" fontSize="11.5" fontFamily="'Space Grotesk', sans-serif" fontWeight="600">{`Charger ${i + 1}`}</text>
            <text x={594} y={y + 39} textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="9" fontFamily="'Inter', sans-serif">Connected</text>
            <rect x={652} y={y + 13} width={32} height={26} rx="7" fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
            <text x={668} y={y + 30} textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize="9.5" fontFamily="'Inter', sans-serif">EV</text>
          </g>
        ))}
      </svg>
    </div>
  );
}
